import * as React from "react";
import { useState } from "react";
import { documentService, type DocumentDto } from "../../../services/documentService";
import { splitExt, umbenennenFehlertext } from "./ereignisAktionen";
import { NamenEingabeDialog, SchraegstrichHinweis } from "../../redesign/NamenEingabeDialog";
import { dateinameOhneSchraegstrich, hatSchraegstrich } from "../../../utils/fileUtils";

/**
 * „Dokument per Rechtsklick umbenennen“ (PO 14.09.2026).
 *
 * Als eigene Komponente, damit die SEITE entscheidet, was nach dem Umbenennen
 * zu tun ist (`ladeVerlauf` + `flashSaved`).
 *
 * ══ DIE ENDUNG STEHT FEST ══════════════════════════════════════════════════
 * Editierbar ist nur die Basis; die Endung steht fix daneben. Das Backend
 * erzwingt ohnehin die Extension des Bestands (`sanitizeRenameFileName`) —
 * ein Feld, in dem sich „.pdf“ zu „.docx“ tippen ließe, würde stumm
 * korrigiert, und der Nutzer hielte das Ergebnis für seinen Fehler.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Optik: das gemeinsame {@link NamenEingabeDialog} (User 03.11.2026) — wie in
 * der Dokumentliste der Akte. „/“ im Namen wird zu „-“; das Popup sagt das,
 * während man tippt (T-456).
 */
export const DokumentUmbenennenDialog: React.FC<{
    dokument: Pick<DocumentDto, "id" | "fileName">;
    /** Nach erfolgreichem Speichern — die Seite lädt dann neu. */
    onGespeichert: () => void | Promise<void>;
    onSchliessen: () => void;
}> = ({ dokument, onGespeichert, onSchliessen }) => {
    const { base, ext } = splitExt(dokument.fileName ?? "");
    const [basis, setBasis] = useState(base);
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    const speichern = async () => {
        if (busy) return;
        const neu = dateinameOhneSchraegstrich(basis.trim());
        if (!neu) {
            setFehler("Bitte einen Dateinamen angeben.");
            return;
        }
        if (neu === base) {
            onSchliessen();
            return;
        }
        setBusy(true);
        setFehler(null);
        try {
            await documentService.update(dokument.id, { fileName: neu + ext });
        } catch (err) {
            // Offen bleiben: der getippte Name ist sonst weg, und der Nutzer
            // wüsste nicht einmal, dass nichts ankam.
            console.error("[DokumentUmbenennen] Speichern fehlgeschlagen", err);
            setFehler(umbenennenFehlertext(err));
            setBusy(false);
            return;
        }
        // Kein `setBusy(false)` danach — der Aufrufer nimmt den Dialog jetzt
        // aus dem Baum, ein Zustandswechsel liefe ins Leere.
        await onGespeichert();
        onSchliessen();
    };

    return (
        <NamenEingabeDialog
            titel="Dokument umbenennen"
            feldName="Neuer Dateiname"
            wert={basis}
            onWert={(w) => {
                setBasis(w);
                if (fehler) setFehler(null);
            }}
            endung={ext || undefined}
            ort={<>Bisher: {dokument.fileName}</>}
            hinweis={
                hatSchraegstrich(basis) ? (
                    <SchraegstrichHinweis ergebnis={dateinameOhneSchraegstrich(basis.trim()) + ext} />
                ) : undefined
            }
            fehler={fehler}
            busy={busy}
            onSpeichern={() => void speichern()}
            onAbbrechen={() => {
                if (!busy) onSchliessen();
            }}
        />
    );
};
